"use client";

import { ChangeEvent, ChangeEventHandler, InputEvent, KeyboardEventHandler, KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { useAppDispatch, useAppSelector } from "@/lib/client/storeHooks";
import { formatMoney } from "@/lib/money-utils"
import Trash from "@/components/icons/trash.svg";
import { CartInfo } from "@/types";
import { removeFromCart as removeFromCartRedux, updateQty as updateCartRedux, updateAmounts as updateAmountsRedux, loadCart as loadCartRedux, clear as clearCartRedux } from "@/lib/client/cartSlice";
import { toast } from "sonner";
import { Link } from "@/i18n/navigation";
import HeadingLarge from "../text/heading-large";
import CartQtyInput from "./cart-qty-input";
import CartSubmit from "./cart-submit";
import CartOptions from "./cart-options";
import { FetchResDesctructure } from "@/app/lib/fetch-utils";
import HeadingMedium from "../text/heading-medium";

import ArrowRight from "@/components/icons/arrow-right.svg";

interface CartViewProps {
  readOnlyMode?: boolean;
  altCart?: CartInfo;
  billingEmail?: string;
  companyName?: string;
}

const UPDATE_TIMEOUT = 1000;

const VOC_LINKS = {
  de: "https://www.bazg.admin.ch/de/lenkungsabgabe-fluechtige-organische-verbindungen-voc",
  fr: "https://www.bazg.admin.ch/fr/taxe-incitation-composes-organiques-volatils-cov",
  it: "https://www.bazg.admin.ch/it/tassa-incentivazione-composti-organici-volatili-cov",
  en: "https://www.bazg.admin.ch/en/incentive-fee-volatile-organic-compounds-voc",
};

export default function CartView(props: CartViewProps) {
  const locale = useLocale();
  const t = useTranslations('Cart');
  const cart = props.altCart || useAppSelector(s => s.cart);
  const user = useAppSelector(s => s.session.user);
  const storeDispatch = useAppDispatch();

  const [lockUi, setLockUi] = useState<boolean>(false);
  const [validationStates, setValidationStates] = useState<boolean[]>((new Array((cart.articles || []).length).fill(true)))

  const loadingModalRef = useRef<HTMLDialogElement | null>(null);
  const keyUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const changeUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    //Reload cart if user has changed (e.g., after sign in)
    fetch("/api/cart", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then(FetchResDesctructure<CartInfo | boolean>)
      .then(res => {
        if (res.ok) {
          if (typeof res.body === 'boolean') {
            storeDispatch(clearCartRedux());
          } else {
            console.log(`Received cart info:`, res.body);
            storeDispatch(loadCartRedux(res.body));
          }
        } else {
          toast.error(t('error-load-cart'));
          throw new Error(`HTTP ${res.status}`);
        }
      })
      .catch(err => {
        console.error('Failed to load cart:', err);
        toast.error(t('error-load-cart'));
      });
  }, [user && user.id]);

  useEffect(() => {
    if (lockUi) {
      loadingModalRef.current?.showModal();
    } else {
      loadingModalRef.current?.close();
    }
  }, [lockUi]);

  /* ------------------------// Remove item from cart //------------------------ */
  const removeItem = async (articleId: number) => {
    try {
      setLockUi(true);

      const res = await fetch("/api/cart/remove", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productId: articleId }),
      });

      setLockUi(false);

      if (!res.ok) {
        toast.error(t('error-remove-item'));
        throw new Error(`HTTP ${res.status}`);
      } else {
        storeDispatch(removeFromCartRedux({ articleId }))
        let cartInfo = await res.json() as CartInfo;
        storeDispatch(loadCartRedux(cartInfo));
      }
    } catch (err) {
      console.error('Failed to remove item:', err);
      toast.error(t('error-remove-item'));
    }
  };

  const minusQtyHandlerFactory = (articleId: number) => {
    return async () => {
      setLockUi(true);
      let articleItem = cart.articles.find(a => a.item.articleId === articleId);
      if (!articleItem) {
        console.error(`Cannot find article with id ${articleId} in the cart`);
        setLockUi(false);
        return;
      }

      let newQty = articleItem.qty - 1;
      if (newQty === 0) {
        const res = await fetch("/api/cart/remove", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ productId: articleId }),
        });

        setLockUi(false);

        if (!res.ok) {
          toast.error(t('error-remove-item'));
          throw new Error(`HTTP ${res.status}`);
        } else {
          storeDispatch(removeFromCartRedux({ articleId }))
          let cartInfo = await res.json() as CartInfo;
          storeDispatch(loadCartRedux(cartInfo));
        }
      } else {
        const res = await fetch("/api/cart/update", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates: [{ productId: articleId, qty: newQty }] }),
        });

        setLockUi(false);

        if (!res.ok) {
          toast.error(t('error-update-quantity'));
          throw new Error(`HTTP ${res.status}`);
        } else {
          storeDispatch(updateCartRedux({ articleId, newQty }))
          let cartInfo = await res.json() as CartInfo;
          storeDispatch(loadCartRedux(cartInfo));
        }
      }
    };
  };

  const plusQtyHandlerFactory = (articleId: number) => {
    return async () => {
      setLockUi(true);
      let articleItem = cart.articles.find(a => a.item.articleId === articleId);
      if (!articleItem) {
        console.error(`Cannot find article with id ${articleId} in the cart`);
        setLockUi(false);
        return;
      }

      let newQty = articleItem.qty + 1;

      const res = await fetch("/api/cart/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates: [{ productId: articleId, qty: newQty }] }),
      });

      if (!res.ok) {
        setLockUi(false);
        toast.error(t('error-update-quantity'));
        throw new Error(`HTTP ${res.status}`);
      } else {
        storeDispatch(updateCartRedux({ articleId, newQty }))
        let cartInfo = await res.json() as CartInfo;
        storeDispatch(loadCartRedux(cartInfo));
      }

      setLockUi(false);
    };
  };

  const newQtyKeyInputHandlerFactory = (articleId: number, cartItemIndex: number): KeyboardEventHandler<HTMLInputElement> => {
    return async (e: KeyboardEvent) => {
      const newQty = parseInt((e.target as HTMLInputElement).value);

      const handler = async () => {
        if (isNaN(newQty)) {
          let newValidationStates = [...validationStates];
          newValidationStates[cartItemIndex] = false;
          setValidationStates(newValidationStates);
          return;
        } else {
          if (validationStates[cartItemIndex] === false) {
            let newValidationStates = [...validationStates];
            newValidationStates[cartItemIndex] = true;
            setValidationStates(newValidationStates)
          }
        }

        setLockUi(true);

        const res = await fetch("/api/cart/update", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates: [{ productId: articleId, qty: newQty }] }),
        });

        setLockUi(false);
        if (!res.ok) {
          toast.error(t('error-update-quantity'));
          throw new Error(`HTTP ${res.status}`);
        } else {
          storeDispatch(updateCartRedux({ articleId, newQty }))
          let cartInfo = await res.json() as CartInfo;
          storeDispatch(loadCartRedux(cartInfo));
        }
      }

      if (keyUpdateTimeoutRef.current) {
        clearTimeout(keyUpdateTimeoutRef.current);
        keyUpdateTimeoutRef.current = null;
      }

      if (e.key === "Enter") {
        await handler();
      } else {
        keyUpdateTimeoutRef.current = setTimeout(handler, UPDATE_TIMEOUT)
      }
    };
  }

  const newQtyChangeHandlerFactory = (articleId: number, cartItemIndex: number): ChangeEventHandler<HTMLInputElement> => {
    return async (e: ChangeEvent) => {
      const newQty = parseInt((e.target as HTMLInputElement).value);

      const handler = async () => {
        if (isNaN(newQty)) {
          let newValidationStates = [...validationStates];
          newValidationStates[cartItemIndex] = false;
          setValidationStates(newValidationStates);
          return;
        } else {
          if (validationStates[cartItemIndex] === false) {
            let newValidationStates = [...validationStates];
            newValidationStates[cartItemIndex] = true;
            setValidationStates(newValidationStates)
          }
        }

        setLockUi(true);

        const res = await fetch("/api/cart/update", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates: [{ productId: articleId, qty: newQty }] }),
        });

        setLockUi(false);
        if (!res.ok) {
          toast.error(t('error-update-quantity'));
          throw new Error(`HTTP ${res.status}`);
        } else {
          storeDispatch(updateCartRedux({ articleId, newQty }))
          let cartInfo = await res.json() as CartInfo;
          storeDispatch(loadCartRedux(cartInfo));
        }
      }

      if (changeUpdateTimeoutRef.current) {
        clearTimeout(changeUpdateTimeoutRef.current);
        changeUpdateTimeoutRef.current = null;
      }

      changeUpdateTimeoutRef.current = setTimeout(handler, UPDATE_TIMEOUT)
    };
  }

  if ((cart.articles || []).length === 0) {
    return (
      <div className="flex flex-col w-full h-[300px] md:h-[500px] gap-6 py-32 md:py-64 items-center justify-center">
        <HeadingLarge>{t('empty_heading')}</HeadingLarge>
        <HeadingMedium>{t('empty_paragraph')}</HeadingMedium>
        <div className="flex flex-row justify-center">
          <Link href="/kategorie">
            <button className="btn btn-secondary rounded-full text-white">
              {t('empty_button')}
              <ArrowRight className="h-[18px] w-[18px]  stroke-white" />
            </button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col w-full h-auto gap-y-[30px] py-2.5 md:p-2.5">
        <div className="flex flex-col w-full">
          {cart.articles.map((a, i) => {
            return (
              <div key={`cart-row-${i}`} className="flex flex-col md:flex-row items-start justify-between md:px-4 border-b border-gray-200 md:h-[148px] font-sans">
                <div className="py-2 md:py-4 px-2 w-[136px]">
                  <Image src={`${a.item.imgSrc[0]}?w=${240}&h=${240}`} alt={a.item.name} height={120} width={120} className="rounded-2xl aspect-square object-cover" />
                </div>
                <div className="flex flex-row md:flex-col flex-auto items-start justify-between py-2 md:py-4 px-2 md:h-[152px]">
                  <div className="flex flex-col gap-1 md:gap-2">
                    <Link href={a.item.productPageUrl} className="font-bold text-sm">
                      {a.item.name}
                    </Link>
                    <div className="text-sm">
                      {a.item.description}
                    </div>
                  </div>
                  <button className="btn btn-ghost p-0 hover:bg-white hover:outline-0" onClick={() => removeItem(a.item.articleId)}>
                    <Trash height={14} width={14} />
                  </button>
                </div>
                <div className="flex flex-row justify-around w-full md:flex-row md:w-auto">
                  <div className="text-center gap-2 py-4 px-2 w-[116px]">
                    <div className="md:py-2.5 text-sm">
                      {formatMoney(a.unitPrice)}
                    </div>
                  </div>
                  <div className="flex justify-center gap-0.5 md:gap-2 py-2 md:py-4 px-1">
                    {props.readOnlyMode ? (
                      <div className="text-sm w-full md:w-[140px]">
                        {a.qty}x
                      </div>
                    ) : (
                      <div className="join w-full md:w-[140px]">
                        <button className="btn btn-sm md:btn-md join-item rounded-l-full" onClick={minusQtyHandlerFactory(a.item.articleId)} {...(lockUi ? { disabled: true } : {})}>
                          -
                        </button>
                        <CartQtyInput qty={a.qty} onChange={newQtyChangeHandlerFactory(a.item.articleId, i)} onKeyUp={newQtyKeyInputHandlerFactory(a.item.articleId, i)} lockUi={lockUi} />
                        {/*<input className="input join-item w-full" type="number" value={a.qty} onChange={newQtyChangeHandlerFactory(a.item.articleId, i)} onKeyUp={newQtyKeyInputHandlerFactory(a.item.articleId, i)} {...(lockUi ? { disabled: true } : {})} />*/}
                        <button className="btn btn-sm md:btn-md join-item rounded-r-full" onClick={plusQtyHandlerFactory(a.item.articleId)} {...(lockUi ? { disabled: true } : {})}>
                          +
                        </button>
                      </div>
                    )
                    }
                  </div>
                  <div className="text-center gap-2 py-4 px-2 w-[116px]">
                    <div className="md:py-2.5 text-sm">
                      {formatMoney(a.unitPrice * a.qty)}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div >
        <div className="flex flex-col-reverse md:flex-row gap-2.5">
          <div className="flex-auto">
            <CartOptions lockUi={lockUi} setLockUi={setLockUi} billingEmail={props.billingEmail} companyName={props.companyName} />
          </div>
          <div className="w-[346px] flex flex-col gap-y-[30px]">
            <table className="table w-[336px]">
              <tbody>
                <tr>
                  <td>
                    {t('cart-subtotal')}
                  </td>
                  <td className="text-right">
                    {formatMoney(cart.total)}
                  </td>
                </tr>
                <tr>
                  <td>
                    {t('cart-shipping-costs')}
                  </td>
                  <td className="text-right">
                    {formatMoney(cart.additionalCosts.shipping)}
                  </td>
                </tr>
                <tr>
                  <td>
                    <a href={VOC_LINKS[locale as "de" | "fr" | "it" | "en"]}>{t('cart-voc-costs')}</a>
                  </td>
                  <td className="text-right">
                    {formatMoney(cart.additionalCosts.VOC)}
                  </td>
                </tr>
                <tr>
                  <td>
                    {t('cart-vat')}
                  </td>
                  <td className="text-right">
                    {formatMoney(cart.additionalCosts.VAT)}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="text-black">
                  <td>
                    {t('cart-total')}
                  </td>
                  <td className="text-right">
                    {formatMoney(cart.grossTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
            <CartSubmit lockUi={lockUi} setLockUi={setLockUi} />
          </div>
        </div>
      </div >
      <dialog id="loading_modal" className="modal" ref={loadingModalRef}>
        <div className="modal-box w-11/12 xl:w-8/12">
          <div className="h-[300px] flex flex-col items-center justify-center">
            <span className="loading loading-spinner loading-xl text-primary"></span>
          </div>
        </div>
      </dialog>
    </>
  )
}
